import {
  type AgenticRequestorRule,
  ApprovalRule,
  type AutoApprover,
  Filter,
  IntegrationResourceRule,
  RequestorRule,
  ResourceRule,
  type TicketApprover,
} from "@p0security/shared/types/workflow/types";
import { cloneDeep, omit } from "lodash";
import { assertNever } from "utils/assert";

export const newResourceRuleOfType = (
  type: ResourceRule["type"]
): ResourceRule => {
  switch (type) {
    case "any":
      return { type };
    case "integration":
      return { type, service: "aws", accessType: "any", filters: {} };
    default:
      return assertNever(type);
  }
};

export const updateResourceService = (
  service: IntegrationResourceRule["service"],
  resource: ResourceRule
): ResourceRule => {
  if (resource.type !== "integration") {
    return resource;
  }
  return { type: "integration", service, accessType: "any", filters: {} };
};

export const updateResourceAccessType = <T extends IntegrationResourceRule>(
  accessType: T["accessType"],
  resource: T
): T => {
  if (!resource.service) return resource;
  return { ...resource, accessType, filters: {} };
};

export const newResourceRuleWithFilter = <T extends IntegrationResourceRule>(
  resource: T,
  filterKey: keyof T["filters"],
  filter: Filter,
  oldFilterKey?: keyof T["filters"]
): T => {
  const { service } = resource;
  if (!service) {
    return resource;
  }
  const currentFilters = cloneDeep(resource.filters) || {};
  const newFilters = {
    ...(oldFilterKey ? omit(currentFilters, oldFilterKey) : currentFilters),
    [filterKey]: filter,
  };
  return {
    ...resource,
    filters: newFilters,
  };
};

export const newResourceRuleWithoutFilter = <T extends IntegrationResourceRule>(
  resource: T,
  filterKey: string
): T => {
  const { service } = resource;
  if (!service) {
    return resource;
  }
  const currentFilters = resource.filters || {};
  return {
    ...resource,
    filters: omit(currentFilters, filterKey),
  };
};

export function newRequestorRuleOfType<T extends RequestorRule["type"]>(
  type: T
): Extract<RequestorRule, { type: T }>;
export function newRequestorRuleOfType(
  type: RequestorRule["type"]
): RequestorRule {
  switch (type) {
    case "agentic":
      return { type, agent: { type: "any" }, user: { type: "any" } };
    case "any":
      return { type };
    case "user":
      return { type, uid: "" };
    case "group":
      return {
        type,
        groups: [{ id: "", label: "", directory: "okta" }],
        effect: "keep",
      };
    case "wif":
      return {
        type,
        provider: "gcp",
        parent: "",
        pool: "",
      };
    default:
      return assertNever(type);
  }
}

export const newAgentRule = (
  type: AgenticRequestorRule["agent"]["type"]
): AgenticRequestorRule["agent"] => {
  switch (type) {
    case "any":
      return { type };
    case "agent":
      return { type, azp: "" };
    case "agent-owner":
      return { type, owner: "" };
    default:
      return assertNever(type);
  }
};

export const newApprovalRuleOfType = (
  type: ApprovalRule["type"],
  installed: { auto: AutoApprover[]; ticket: TicketApprover[] }
): ApprovalRule | undefined => {
  const [firstAuto] = installed.auto;
  const [firstTicket] = installed.ticket;
  switch (type) {
    case "persistent":
    case "deny":
      return { type };
    case "p0":
      return { type, options: {} };
    case "auto":
      return firstAuto && { type, integration: firstAuto, options: {} };
    case "ticket":
      return (
        firstTicket && {
          type,
          integration: firstTicket,
          namespace: "",
          mapping: {},
          options: {},
        }
      );
    case "group":
      return {
        type,
        groups: [{ id: "", label: "", directory: "okta" }],
        effect: "keep",
        options: {},
      };
    case "escalation":
      return firstAuto && { type, integration: firstAuto, services: [] };
    case "requestor-profile":
      return { type, directory: "okta", options: {} };
    default:
      return assertNever(type);
  }
};
